import React, { useState } from 'react';
import {
  HiOutlineSparkles,
  HiOutlineCommandLine,
  HiOutlineSquares2X2,
  HiOutlineCheck,
  HiOutlineCheckCircle,
  HiOutlineClipboardDocument,
} from 'react-icons/hi2';
import { copyText } from '../utils/clipboard';
import { buildClaudeCodeCommand, buildMcpConfigJson, buildTokenizedUrl } from '../utils/mcpSnippets';

// ============================================================================
// "Token created" — what you see the moment an MCP access token exists.
// ----------------------------------------------------------------------------
// Task-first: pick where you're connecting, copy ONE thing, paste it there.
// Every snippet embeds the token, so copying a snippet IS saving it — which is
// why the copy control is the loudest thing on screen and the "shown once"
// note sits right under it.
//
// Shared by both surfaces that mint tokens — the Integrations card's
// CreateTokenModal and the /connect wizard — so the instructions for a given
// client can't drift between them. Each caller supplies its own chrome (header,
// footer, buttons); everything inside is identical, deliberately.
// ============================================================================

// Token-first clients lead. Both places this panel appears are reached by
// people connecting over MCP — the Integrations MCP card exists because Claude
// has its own card beside it, and the /connect wizard steers Claude users to
// sign-in before a token is ever created. Claude keeps a tab as the fallback
// for anyone who minted one anyway; it just doesn't get the default slot.
const CLIENTS = [
  { key: 'other', label: 'Any MCP app', Icon: HiOutlineSquares2X2 },
  { key: 'claude-code', label: 'Claude Code', Icon: HiOutlineCommandLine },
  { key: 'claude', label: 'Claude', Icon: HiOutlineSparkles },
];

// One big obvious copy target — the "what you copy" of the created state.
const PrimarySnippet = ({ value, copyLabel = 'Copy', onCopy }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    copyText(value).then(() => {
      setCopied(true);
      onCopy?.();
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="rounded-xl border border-gray-200 dark:border-white/[0.08] bg-gray-50/80 dark:bg-white/[0.03] overflow-hidden">
      <pre className="text-xs font-mono text-gray-700 dark:text-gray-300 px-3.5 pt-3 pb-3 whitespace-pre-wrap break-all select-all m-0 leading-relaxed">{value}</pre>
      <div className="px-2 pb-2">
        <button
          onClick={handleCopy}
          className={`w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold text-white rounded-lg transition-all duration-200 ${
            copied
              ? 'bg-emerald-500'
              : 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-400 hover:to-cyan-400 shadow-sm shadow-cyan-500/25'
          }`}
        >
          {copied ? (
            <>
              <HiOutlineCheck className="w-4 h-4" strokeWidth={2.5} />
              Copied — now paste it
            </>
          ) : (
            <>
              <HiOutlineClipboardDocument className="w-4 h-4" />
              {copyLabel}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// A quiet step — a small dot instead of a numbered badge keeps the list calm.
const StepRow = ({ children }) => (
  <li className="flex items-start gap-2.5">
    <span className="flex-shrink-0 w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-600 mt-2" aria-hidden="true" />
    <span className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{children}</span>
  </li>
);

// Highlights a spot in the client's UI ("Settings", "Add custom connector") so
// the eye can match it against the real app without parsing the sentence.
const Ui = ({ children }) => (
  <span className="font-semibold text-gray-900 dark:text-white whitespace-nowrap">{children}</span>
);

const CopyField = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">{label}</h4>
      <div className="flex items-start gap-2">
        <code className="flex-1 text-xs bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/50 rounded-lg px-3 py-2.5 font-mono text-gray-700 dark:text-gray-300 break-all select-all">
          {value}
        </code>
        <button
          onClick={() => copyText(value).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })}
          className="flex-shrink-0 px-2.5 py-1.5 text-xs font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/50 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
    </div>
  );
};

// `oauthAvailable`: the OAuth AS is live, so Claude connects by sign-in and the
// tokenized URL is demoted to a fallback for clients that can't set headers.
const McpTokenSetup = ({ token, mcpUrl, oauthAvailable = false, onCopied }) => {
  const [clientTab, setClientTab] = useState(null);
  const [copiedSnippet, setCopiedSnippet] = useState(false);
  const active = clientTab || CLIENTS[0].key;

  const markCopied = () => {
    setCopiedSnippet(true);
    onCopied?.();
  };

  const connectorUrl = buildTokenizedUrl(mcpUrl, token);

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2.5">
          Where are you connecting?
        </h4>
        <div className="grid grid-cols-3 gap-2">
          {CLIENTS.map(({ key, label, Icon }) => {
            const selected = active === key;
            return (
              <button
                key={key}
                onClick={() => setClientTab(key)}
                className={`flex items-center justify-center gap-2 px-2 py-2.5 rounded-xl border text-center transition-all duration-200 ${
                  selected
                    ? 'bg-cyan-50 dark:bg-cyan-500/10 border-cyan-400/70 dark:border-cyan-400/50'
                    : 'bg-white dark:bg-gray-800/40 border-gray-200 dark:border-gray-700/40 hover:border-gray-300 dark:hover:border-gray-600/60 hover:bg-gray-50 dark:hover:bg-gray-800/60'
                }`}
              >
                <Icon className={`w-4 h-4 flex-shrink-0 transition-colors duration-200 ${selected ? 'text-cyan-500 dark:text-cyan-400' : 'text-gray-400 dark:text-gray-500'}`} />
                <span className={`text-xs font-semibold leading-tight ${selected ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>
                  {label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {active === 'claude-code' && (
        <div className="space-y-4">
          <PrimarySnippet copyLabel="Copy command" value={buildClaudeCodeCommand(mcpUrl, token)} onCopy={markCopied} />
          <ul className="space-y-2">
            <StepRow>Paste it into your terminal and press <Ui>Enter</Ui>.</StepRow>
            <StepRow>Start <code className="font-mono">claude</code> and ask it about your data.</StepRow>
          </ul>
        </div>
      )}

      {active === 'other' && (
        <div className="space-y-4">
          <PrimarySnippet copyLabel="Copy config" value={buildMcpConfigJson(mcpUrl, token)} onCopy={markCopied} />
          <ul className="space-y-2">
            <StepRow>
              Cursor: <Ui>Settings</Ui> → <Ui>MCP</Ui> → <Ui>Add server</Ui>. Other apps: add it to your{' '}
              <code className="font-mono">mcp.json</code>.
            </StepRow>
            <StepRow>Then ask your agent to scrape something — it finds the tools on its own.</StepRow>
          </ul>
        </div>
      )}

      {active === 'claude' && (oauthAvailable ? (
        <div className="space-y-4">
          <PrimarySnippet copyLabel="Copy URL" value={mcpUrl} onCopy={markCopied} />
          <ul className="space-y-2">
            <StepRow>
              In Claude, open <Ui>Settings</Ui> → <Ui>Connectors</Ui> and click <Ui>Add custom connector</Ui>.
            </StepRow>
            <StepRow>
              Paste the URL, click <Ui>Add</Ui>, then <Ui>Connect</Ui> and sign in.
            </StepRow>
          </ul>
        </div>
      ) : (
        <div className="space-y-4">
          <PrimarySnippet copyLabel="Copy link" value={connectorUrl} onCopy={markCopied} />
          <ul className="space-y-2">
            <StepRow>
              In Claude, open <Ui>Settings</Ui> → <Ui>Connectors</Ui> and click <Ui>Add custom connector</Ui>.
            </StepRow>
            <StepRow>
              Paste the link, click <Ui>Add</Ui> — then ask Claude about your data.
            </StepRow>
          </ul>
        </div>
      ))}

      {/* The snippets above all contain the token, so copying one IS saving it.
          Before copying we quietly note it's shown once; after, we confirm. */}
      {copiedSnippet ? (
        <p className="flex items-center gap-2 text-xs text-emerald-600 dark:text-emerald-400 leading-relaxed">
          <HiOutlineCheckCircle className="w-4 h-4 flex-shrink-0" />
          Copied — safe to close this once you're connected.
        </p>
      ) : (
        <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
          Your token is shown only once. Copy it before closing.
        </p>
      )}

      <details className="text-xs text-gray-400 dark:text-gray-500 pt-1">
        <summary className="cursor-pointer select-none hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
          Just the raw token{oauthAvailable ? ' / tokenized URL' : ''}
        </summary>
        <div className="mt-2 space-y-3">
          <CopyField label="Your token" value={token} />
          {oauthAvailable && <CopyField label="Tokenized URL (only for clients that can't set headers)" value={connectorUrl} />}
        </div>
      </details>
    </div>
  );
};

export default McpTokenSetup;
